/**
 * Convierte HTML (TipTap u otro) a bloques de blog.
 */

import type { BlogBlock } from './types';

function getTextContent(el: Element): string {
    return el.textContent?.trim() ?? '';
}

function getInnerHtml(el: Element): string {
    return el.innerHTML.trim();
}

function createBlock<T extends BlogBlock>(block: T): T {
    return block;
}

export function htmlToBlocks(html: string): BlogBlock[] {
    if (!html?.trim()) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;
    const blocks: BlogBlock[] = [];
    let order = 0;

    const walk = (node: Node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const el = node as Element;

        if (el.tagName === 'H1') {
            blocks.push(
                createBlock({
                    id: crypto.randomUUID(),
                    type: 'heading',
                    order: order++,
                    level: 1,
                    content: getInnerHtml(el),
                })
            );
            return;
        }
        if (el.tagName === 'H2') {
            blocks.push(
                createBlock({
                    id: crypto.randomUUID(),
                    type: 'heading',
                    order: order++,
                    level: 2,
                    content: getInnerHtml(el),
                })
            );
            return;
        }
        if (el.tagName === 'H3') {
            blocks.push(
                createBlock({
                    id: crypto.randomUUID(),
                    type: 'heading',
                    order: order++,
                    level: 3,
                    content: getInnerHtml(el),
                })
            );
            return;
        }
        if (el.tagName === 'P') {
            const content = getInnerHtml(el);
            if (content.length > 0) {
                blocks.push(
                    createBlock({
                        id: crypto.randomUUID(),
                        type: 'paragraph',
                        order: order++,
                        content,
                    })
                );
            }
            return;
        }
        if (el.tagName === 'IMG') {
            const src = (el as HTMLImageElement).getAttribute('src') ?? '';
            const alt = (el as HTMLImageElement).getAttribute('alt') ?? '';
            if (src) {
                blocks.push(
                    createBlock({
                        id: crypto.randomUUID(),
                        type: 'image',
                        order: order++,
                        url: src,
                        alt: alt || undefined,
                    })
                );
            }
            return;
        }
        if (el.tagName === 'UL') {
            const items = Array.from(el.querySelectorAll(':scope > li')).map(
                (li) => getInnerHtml(li)
            );
            if (items.length > 0) {
                blocks.push(
                    createBlock({
                        id: crypto.randomUUID(),
                        type: 'list',
                        order: order++,
                        listType: 'bullet',
                        items,
                    })
                );
            }
            return;
        }
        if (el.tagName === 'OL') {
            const items = Array.from(el.querySelectorAll(':scope > li')).map(
                (li) => getInnerHtml(li)
            );
            if (items.length > 0) {
                blocks.push(
                    createBlock({
                        id: crypto.randomUUID(),
                        type: 'list',
                        order: order++,
                        listType: 'ordered',
                        items,
                    })
                );
            }
            return;
        }
        if (el.tagName === 'BLOCKQUOTE') {
            const p = el.querySelector('p');
            const content = p ? getInnerHtml(p) : getInnerHtml(el);
            if (content.length > 0) {
                blocks.push(
                    createBlock({
                        id: crypto.randomUUID(),
                        type: 'blockquote',
                        order: order++,
                        content,
                    })
                );
            }
            return;
        }

        // Recorrer hijos si no es un bloque directo
        for (const child of el.childNodes) {
            walk(child);
        }
    };

    // Recorrer hijos directos del body
    for (const child of body.childNodes) {
        walk(child);
    }

    return blocks;
}
